import { useState, useEffect, useCallback } from "react";
import { BridgeEvent, NativeBridgeAvailable } from "@unu/ui-enums";
import { sendEvent } from "@unu/ui-common";
import { registerEvents } from "@unu/web-native-bridge";

/**
 * Health data types supported by the SDK
 */
export type HealthDataType =
  | "heart_rate"
  | "resting_heart_rate"
  | "hrv"
  | "blood_pressure_systolic"
  | "blood_pressure_diastolic"
  | "blood_oxygen"
  | "respiratory_rate"
  | "body_temperature"
  | "weight"
  | "height"
  | "bmi"
  | "body_fat"
  | "lean_body_mass"
  | "steps"
  | "distance"
  | "active_energy"
  | "exercise_time"
  | "stand_time"
  | "flights_climbed"
  | "sleep_analysis"
  | "sleep_duration"
  | "dietary_energy"
  | "dietary_water"
  | "mindful_minutes";

/**
 * Health record from native platform
 */
export interface HealthRecord {
  platform: "healthkit" | "health_connect";
  sourceId: string;
  dataType: HealthDataType;
  value: number;
  unit: string;
  timestamp: string;
  endTimestamp?: string;
  source: string;
  metadata?: Record<string, unknown>;
}

/**
 * Permission status for a data type
 */
export interface DataTypePermission {
  type: HealthDataType;
  read: "not_determined" | "authorized" | "denied" | "restricted" | "limited";
  write?: "not_determined" | "authorized" | "denied" | "restricted" | "limited";
}

/**
 * Health permissions state
 */
export interface HealthPermissions {
  dataTypes: DataTypePermission[];
  allGranted: boolean;
  hasAnyGranted: boolean;
  hasDenied: boolean;
  lastChecked: string;
}

/**
 * Health error
 */
export interface HealthError {
  code: string;
  message: string;
  action?: string;
}

/**
 * Health platform status
 */
export interface HealthStatus {
  platform: "healthkit" | "health_connect";
  isConnected: boolean;
  isAvailable: boolean;
  isLoading: boolean;
  isFetching: boolean;
  permissions: HealthPermissions | null;
  error: HealthError | null;
}

/**
 * Options for fetching health data
 */
export interface FetchHealthDataOptions {
  dataTypes?: HealthDataType[];
  startDate?: Date;
  endDate?: Date;
  limit?: number;
}

/**
 * Hook return type
 */
export interface UseNativeHealthSDKResult {
  // Platform state
  status: HealthStatus | null;
  isAvailable: boolean;
  isConnected: boolean;
  hasAnyPermissions: boolean;
  isLoading: boolean;
  permissions: HealthPermissions | null;
  error: HealthError | null;

  // Data state
  records: HealthRecord[];
  isFetching: boolean;

  // Actions
  requestPermissions: (dataTypes?: HealthDataType[]) => void;
  fetchHealthData: (options?: FetchHealthDataOptions) => void;
  refreshStatus: () => void;

  // Helpers
  getRecordsByType: (dataType: HealthDataType) => HealthRecord[];
  getLatestRecord: (dataType: HealthDataType) => HealthRecord | null;
  clearRecords: () => void;
}

/**
 * useNativeHealthSDK
 *
 * Hook for web app to interact with native health SDK (HealthKit/Health Connect)
 * via the webview bridge.
 *
 * @example
 * ```tsx
 * const {
 *   isConnected,
 *   isAvailable,
 *   permissions,
 *   records,
 *   isFetching,
 *   requestPermissions,
 *   fetchHealthData,
 * } = useNativeHealthSDK();
 *
 * // Request permissions
 * requestPermissions(['heart_rate', 'steps', 'weight']);
 *
 * // Fetch data
 * fetchHealthData({
 *   dataTypes: ['heart_rate', 'steps'],
 *   startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
 *   endDate: new Date(),
 * });
 * ```
 */
export function useNativeHealthSDK(): UseNativeHealthSDKResult {
  const [status, setStatus] = useState<HealthStatus | null>(null);
  const [records, setRecords] = useState<HealthRecord[]>([]);
  const [error, setError] = useState<HealthError | null>(null);

  const handleStatusUpdate = useCallback((data: any) => {
    setStatus({
      platform: data.platform,
      isConnected: data.isConnected ?? false,
      isAvailable: data.isAvailable ?? false,
      isLoading: data.isLoading ?? false,
      isFetching: data.isFetching ?? false,
      permissions: data.permissions ?? null,
      error: data.error ?? null,
    });

    if (data.error) {
      setError(data.error);
    }
  }, []);

  const handleDataReceived = useCallback((data: any) => {
    if (data.records && Array.isArray(data.records)) {
      setRecords(data.records);
    }

    if (data.error) {
      setError(data.error);
    }
  }, []);

  const handlePermissionsResult = useCallback((data: any) => {
    if (data.permissions) {
      setStatus((prev) =>
        prev
          ? {
              ...prev,
              permissions: data.permissions,
              isConnected: data.permissions.allGranted,
            }
          : null
      );
    }

    if (data.error) {
      setError(data.error);
    }
  }, []);

  const handleError = useCallback((data: any) => {
    setError({
      code: data.code ?? "UNKNOWN_ERROR",
      message: data.message ?? "An unknown error occurred",
      action: data.action,
    });
  }, []);

  useEffect(() => {
    if (!NativeBridgeAvailable) {
      return;
    }

    const events = {
      [BridgeEvent.HealthStatusUpdate]: handleStatusUpdate,
      [BridgeEvent.HealthDataReceived]: handleDataReceived,
      [BridgeEvent.HealthPermissionsResult]: handlePermissionsResult,
      [BridgeEvent.HealthError]: handleError,
    };

    return registerEvents(events);
  }, [
    handleStatusUpdate,
    handleDataReceived,
    handlePermissionsResult,
    handleError,
  ]);

  useEffect(() => {
    if (NativeBridgeAvailable) {
      sendEvent({
        name: BridgeEvent.SendData,
        data: {
          name: BridgeEvent.HealthGetStatus,
        },
      });
    }
  }, []);

  const requestPermissions = useCallback((dataTypes?: HealthDataType[]) => {
    if (!NativeBridgeAvailable) {
      setError({
        code: "BRIDGE_UNAVAILABLE",
        message: "Native bridge is not available",
      });
      return;
    }

    sendEvent({
      name: BridgeEvent.SendData,
      data: {
        name: BridgeEvent.HealthRequestPermissions,
        data: { dataTypes },
      },
    });
  }, []);

  const fetchHealthData = useCallback((options?: FetchHealthDataOptions) => {
    if (!NativeBridgeAvailable) {
      setError({
        code: "BRIDGE_UNAVAILABLE",
        message: "Native bridge is not available",
      });
      return;
    }

    sendEvent({
      name: BridgeEvent.SendData,
      data: {
        name: BridgeEvent.HealthFetchData,
        data: {
          dataTypes: options?.dataTypes,
          startDate: options?.startDate?.toISOString(),
          endDate: options?.endDate?.toISOString(),
          limit: options?.limit,
        },
      },
    });
  }, []);

  const refreshStatus = useCallback(() => {
    if (!NativeBridgeAvailable) {
      return;
    }

    sendEvent({
      name: BridgeEvent.SendData,
      data: {
        name: BridgeEvent.HealthGetStatus,
      },
    });
  }, []);

  const getRecordsByType = useCallback(
    (dataType: HealthDataType): HealthRecord[] => {
      return records.filter((record) => record.dataType === dataType);
    },
    [records]
  );

  const getLatestRecord = useCallback(
    (dataType: HealthDataType): HealthRecord | null => {
      const typeRecords = records
        .filter((record) => record.dataType === dataType)
        .sort(
          (a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );

      return typeRecords[0] ?? null;
    },
    [records]
  );

  const clearRecords = useCallback(() => {
    setRecords([]);
    setError(null);
  }, []);

  return {
    // Platform state
    status,
    isAvailable: status?.isAvailable ?? false,
    isConnected:
      status?.isConnected ?? status?.permissions?.hasAnyGranted ?? false,
    hasAnyPermissions: status?.permissions?.hasAnyGranted ?? false,
    isLoading: status?.isLoading ?? false,
    permissions: status?.permissions ?? null,
    error,
    // Data state
    records,
    isFetching: status?.isFetching ?? false,
    // Actions
    requestPermissions,
    fetchHealthData,
    refreshStatus,
    // Helpers
    getRecordsByType,
    getLatestRecord,
    clearRecords,
  };
}

export default useNativeHealthSDK;
